import {
  LogType,
  LoggerEventType,
  DebugURLParams,
  ComponentSourceType
} from "../../common/schemas/enums";

import { 
  postDebugLogEvent 
} from "./api";

type CSSMap = {
  [key in LogType]: string
}

type CXInjectorWindowDebug = {
  [key in DebugURLParams]?: string
}

interface DebugLogData{
    sessionId: string,
    logType: string,
    order: number,
    debugLog: any[]
}

export class LogManager{
  private _logMode: string = `${LogType.INFO}`;
  private _componentSource: string = '';
  private _debugLogGroupId: string;
  private _analyticsSessionId: string;
  private _toggleRemoteLogging: Boolean = false;
  private topWindow: Window = window;
  public windowURL: string = window.location.href;
  public paramString: string;
  public paramArray: string[];
  private storedLogs: any[] = [];
  private startTime: Date = new Date();
  private timestampColor: {[key:string]:number} = {
    r:Math.floor(Math.random()*130+20),
    g:Math.floor(Math.random()*130+20),
    b:Math.floor(Math.random()*130+20)
  }
  private debugColors: CSSMap = { 
    [LogType.CXO]: 'background-color: #512da8; color: #FFF;',
    [LogType.QA]: 'background-color:rgba(255, 255, 217,1); color:rgb(64, 0, 255);',
    [LogType.INFO]: 'background-color:rgba(255,255,255,1); color: rgba(0,100,0, 1);',
    [LogType.INSERT]: 'background-color: rgba(229,239,229, 1); color: rgb(0,100,0);',
    [LogType.ERROR]: 'background-color: rgba(255,229,229, 1); color: rgba(255,0,0, 1);',
    [LogType.IMPORTANT]: 'background-color: rgba(199,5,5); color: rgba(255,255,255, 1);',
    [LogType.HIGHLIGHT]: 'background-color: rgba(255,255,0); color: rgba(0,0,0, .75);',
    [LogType.OVERLAYLOGS]: 'background-color: rgba(3,22,52, 1); color: rgba(242,221,203, 1);',
    [LogType.TAM]: 'background-color: rgba(230,254,255,1); color: rgba(23,0,135, 1);',
    [LogType.ALL]: '',
    [LogType.NONE]: ''
  }
  private showTimestamp: boolean = false;
  private highlightLogs: boolean = false;
  private logFunc: Function;
  private externalLogOrder: number = 0;
  
  constructor() {
    this.logFunc = (console.log.toString().includes('native code'))?console.log:(console.info.toString().includes('native code'))?console.info:null;

    if((!this.windowURL.includes('DEBUG') && !this.windowURL.includes(DebugURLParams.CX_TIER)) && window.self !== window.top){
      try{
        this.windowURL = window.top.location.href;
        this.topWindow = window.top;
      }catch(err){
        this.windowURL = window.document.referrer || window.location?.ancestorOrigins?.[window.location.ancestorOrigins.length-1] || this.windowURL;
        this.evaluateDebugLog([`unfriendly iframe detected at log manager url - referrer url determined: ${this.windowURL}`], LogType.ERROR);
      }
    }

    if(this.windowURL.includes('%')){
      this.windowURL = decodeURI(this.windowURL);
      this.windowURL = (this.windowURL.includes('%'))?decodeURIComponent(this.windowURL):this.windowURL;
    }

    this.paramString = this.windowURL.substring(this.windowURL.indexOf('?')+1) || '';
    this.paramArray = [];
    this.extractQueryParams(this.paramString);

    const logModeParamValue = this.getDebugParamValue(DebugURLParams.CX_LOG_MODE);
    if(logModeParamValue !== null){
      this._logMode = logModeParamValue;
      if(this._logMode.includes('most')){
        this._logMode += 'qa-info-boot-insert-error-important';
      }
    //}else if(this.windowURL.includes('localhost') || this.windowURL.includes('file:///') || this.windowURL.includes('127.0.0.1')){
      //this._logMode = 'qa-info-insert-error-boot-important';
    }else if(this.windowURL.includes(DebugURLParams.CX_TIER+'=')){
      this._logMode += '-qa-important-boot-error-';
    }

    if(this.getDebugParamValue(DebugURLParams.CX_LOG_TIMESTAMPS) !== null){
      this.showTimestamp = true;
    }
    const highlightLogsParamValue = this.getDebugParamValue(DebugURLParams.HIGHLIGHT_LOGS);
    if(highlightLogsParamValue !== null && highlightLogsParamValue.includes('t')){
      this.highlightLogs = true;
    }
    if(this.logFunc === null){
      this.evaluateDebugLog('no console logging available - will try to create iframe console.', LogType.IMPORTANT);
      this.createLoggingIframe();
    }
  }

  sendDebugLogToRemote(debugData: any[], lt: string) {
    let debugLogEvent: DebugLogData = {
      sessionId: this.analyticsSessionId,
      order: ++this.externalLogOrder,
      logType: lt,
      debugLog: debugData
    }
    postDebugLogEvent(debugLogEvent);
  }

  extractQueryParams(params: string): void {
    const noPrefix = params.substring(params.indexOf('?')+1);
    let splitParams = noPrefix.split('&');
    
    if(this.paramArray.length > 0){
      splitParams = splitParams.filter(param => {
        const paramName = param.split('=')[0];
        const existingParam = this.paramArray.some(x => x.indexOf(paramName) === 0);
        return !existingParam
      })
    }

    this.paramArray = this.paramArray.concat(splitParams);
    
    const delayedQueryParamsLog = ()=>{this.evaluateDebugLog(['extractQueryParams - Query params in use: ', this.paramArray], LogType.QA);}
    window.setTimeout(delayedQueryParamsLog, 10);
  }

  createLoggingIframe(): void{
    let frame:HTMLIFrameElement = document.createElement('iframe');
    frame.setAttribute('style', 'display:none; position:absolute; width:0px; height:0px;');
    document.body.appendChild(frame);
    let windowWatch: ReturnType<typeof setInterval> = setInterval(()=>{
      if(typeof (frame?.contentWindow as any)?.console !== 'undefined'){
        this.logFunc = (frame.contentWindow as any).console.log;
        this.showStoredLogs();
        this.evaluateDebugLog('Log function created', LogType.IMPORTANT);
        clearInterval(windowWatch);
      }
    }, 10)
  }
  
  get timeStamp(): string{
    const currentDate = new Date();
    const hours = (currentDate.getHours() > 9)?currentDate.getHours():'0'+currentDate.getHours();
    const minutes = (currentDate.getMinutes() > 9)?currentDate.getMinutes():'0'+currentDate.getMinutes();
    const seconds = (currentDate.getSeconds() > 9)?currentDate.getSeconds():'0'+currentDate.getSeconds();
    const milliseconds = (currentDate.getMilliseconds() > 99)?currentDate.getMilliseconds():(currentDate.getMilliseconds() > 9)?'0'+currentDate.getMilliseconds():'00'+currentDate.getMilliseconds();
    return hours + ":" + minutes + ":" + seconds + "." + milliseconds;
  }

  get timeElapsed(): string{
    return (.001*((new Date()).getTime()-this.startTime.getTime())).toFixed(3);
  }

  get logMode(): string{
    return this._logMode;
  }

  set logMode(mode: string){
    this._logMode = mode;
    this.evaluateDebugLog('Set log mode: ' + mode + ' - ' + this.logMode, [LogType.QA]);
  }

  set componentSource(name: string) {
    this._componentSource = name;
  }

  get analyticsSessionId() {
    return this._analyticsSessionId
  }

  set analyticsSessionId(sid: string) {
    this._analyticsSessionId = sid ??  ''
  }

  get toggleRemoteLogging() {
    return this._toggleRemoteLogging
  }

  set toggleRemoteLogging(b: Boolean) {
    this._toggleRemoteLogging = b ??  false
  }

  get componentSource() {
    return this._componentSource
  }

  set debugLogGroupId(gid: string) {
    this._debugLogGroupId = gid ??  ''
  }

  get debugLogGroupId() {
    return this._debugLogGroupId
  }  
  
  evaluateDebugLog(l: string | any[], type: LogType | LogType[], hasTimestamp:string = ''): boolean{
    if(hasTimestamp === '' || this.logFunc === null){
      this.storedLogs.push([l, type, this.timeElapsed]);
    }
    try{
      if(this.logMode.toLowerCase().indexOf(LogType.NONE)===-1){
        type=(typeof type === 'string')?[type]:type;
        let logAll = (this.logMode.toLowerCase().indexOf(LogType.ALL)!==-1)?true:false;
        if(logAll || type.find(t => this.logMode.toLowerCase().indexOf(t.toLowerCase())>-1)){
          let logArr: any[] = (!Array.isArray(l))?[l]:[...l];
          let logCSS: string = '';
          let logType: string = this.logMode.toLowerCase();
          if((this.logMode.toLowerCase().indexOf(LogType.OVERLAYLOGS)!==-1) && type.find(t => t === LogType.OVERLAYLOGS)){
            logCSS = this.debugColors[LogType.OVERLAYLOGS];
            logType = LogType.OVERLAYLOGS;
          }else if((this.logMode.toLowerCase().indexOf(LogType.HIGHLIGHT)!==-1) && type.find(t => t === LogType.HIGHLIGHT)){
            logCSS = this.debugColors[LogType.HIGHLIGHT];
            logType = LogType.HIGHLIGHT;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.IMPORTANT)!==-1) && type.find(t => t === LogType.IMPORTANT)){
            logCSS = this.debugColors[LogType.IMPORTANT];
            logType = LogType.IMPORTANT;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.ERROR)!==-1) && type.find(t => t === LogType.ERROR)){
            logCSS = this.debugColors[LogType.ERROR];
            logType = LogType.ERROR;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.TAM)!==-1) && type.find(t => t === LogType.TAM)){
            logCSS = this.debugColors[LogType.TAM];
            logType = LogType.TAM;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.QA)!==-1) && type.find(t => t === LogType.QA)){
            logCSS = this.debugColors[LogType.QA];
            logType = LogType.QA;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.INFO)!==-1) && type.find(t => t === LogType.INFO)){
            logCSS = this.debugColors[LogType.INFO];
            logType = LogType.INFO;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.CXO)!==-1) && type.find(t => t === LogType.CXO)){
            logCSS = this.debugColors[LogType.CXO];
            logType = LogType.CXO;
          }else if((logAll || this.logMode.toLowerCase().indexOf(LogType.INSERT)!==-1) && type.find(t => t === LogType.INSERT)){
            logCSS = this.debugColors[LogType.INSERT];
            logType = LogType.INSERT;
          }
          if(logCSS !== ''){
            logCSS += (this.highlightLogs)?'padding: 1px 4px; display: inline-block; font-weight:normal;':'padding: 1px 4px; border-radius: 3px; display: inline-block; font-weight:bold;';
            const consoleLogArray = [];
            const largeIndent: string = '      ';
            //const smallIndent: string = '   ';
            const componentLabel: string = (this.componentSource === ComponentSourceType.OVERLAY)?'CX-O':
            (this.componentSource === ComponentSourceType.SLIDESHOW)?'CX-S':
            (this.componentSource === ComponentSourceType.BID_ENRICHMENT)?'CX-B':'NONE';
            const componentLabelColor: string = (this.componentSource === ComponentSourceType.OVERLAY)?'#2c5499':
            (this.componentSource === ComponentSourceType.SLIDESHOW)?'#1d1459':
            (this.componentSource === ComponentSourceType.BID_ENRICHMENT)?'#7c2c99':'#992c58';
            for(let i = 0; i < logArr.length; i++){
              if(consoleLogArray.length === 0){
                const logSub: string = (typeof logArr[i] === 'string')?'%s':(typeof logArr[i] === 'object' && logArr[i] !== null && typeof logArr[i].nodeType === 'undefined')?'%O':'%o';
                consoleLogArray.push(`%cCX%cLOGS.${this.debugLogGroupId}%c${componentLabel}%c${logType.toUpperCase()}%c${((!!hasTimestamp)?hasTimestamp:this.timeElapsed)}%c${logSub}`);
                if(!this.highlightLogs){
                  consoleLogArray.push('padding: 1px 4px; border-radius: 3px; display: inline-block; background-color: #ffb700; color:rgba(255,255,255,1); font-weight:bold;');
                  consoleLogArray.push('font-size:0; color:rgba(255,255,255,0); background-color:rgba(255,255,255,0);');
                  consoleLogArray.push(`padding: 1px 4px; border-radius: 3px; display: inline-block; background-color: ${componentLabelColor}; color:rgba(255,255,255,1); font-weight:bold;`);
                  consoleLogArray.push(logCSS);
                  consoleLogArray.push(`font-size:.8em; font-weight:bold; color:rgba(${this.timestampColor.r},${this.timestampColor.g},${this.timestampColor.b},1); background-color:rgba(255,255,255,0); margin:2px 3px;`);
                  consoleLogArray.push('padding: 1px 4px; border-radius: 3px; display: inline-block; background-color: initial; color:initial; font-weight:normal;');
                }else{
                  consoleLogArray.push('font-size:0; color:rgba(255,255,255,0); background-color:rgba(255,255,255,0);');
                  consoleLogArray.push('font-size:0; color:rgba(255,255,255,0); background-color:rgba(255,255,255,0);');
                  consoleLogArray.push('font-size:0; color:rgba(255,255,255,0); background-color:rgba(255,255,255,0);');
                  consoleLogArray.push('font-size:0; color:rgba(255,255,255,0); background-color:rgba(255,255,255,0);');
                  consoleLogArray.push(`font-size:.8em; font-weight:bold; color:rgba(${this.timestampColor.r},${this.timestampColor.g},${this.timestampColor.b},1); background-color:rgba(255,255,255,0); margin:2px 3px;`);
                  if(logType === LogType.QA){
                    logCSS += 'font-size:1.3em;';
                  }
                  consoleLogArray.push(logCSS);
                }
                if(this.showTimestamp){
                  consoleLogArray.push(this.timeStamp + ' - ' + logArr[i]);
                }else{
                  consoleLogArray.push(logArr[i]);
                }
              }else if(typeof logArr[i] === 'string'){
                consoleLogArray[0] += `\n${largeIndent}%s`;
                consoleLogArray.push(logArr[i]);
              }else{
                const logSub: string = (typeof logArr[i] === 'object' && logArr[i] !== null && typeof logArr[i].nodeType === 'undefined')?'%O':'%o';
                //consoleLogArray[0] += (consoleLogArray[0].substr(consoleLogArray[0].length-2) !== '%s')?'\n'+largeIndent:smallIndent;
                consoleLogArray[0] += '\n'+largeIndent;
                consoleLogArray[0] += logSub;
                //consoleLogArray.push('    '+logSub);
                consoleLogArray.push(logArr[i]);
              }
            }
            //console.log(String(consoleLogArray));
            if(this.logFunc !== null){
              this.logFunc(...consoleLogArray);
            }
            this.dispatchLoggerEvent({
              log:consoleLogArray,
              type:logType
            });

            if (this.toggleRemoteLogging){
              //const consoleLogInfo = consoleLogArray[0].split('%c')
              //const logType = consoleLogInfo.length > 3 ? consoleLogInfo[consoleLogInfo.length - 3] : null

              for(let i = 0; i < logArr.length; i++){
                if(typeof logArr[i] === 'object' && logArr[i] !== null)
                  if(typeof logArr[i].nodeType === 'undefined'){
                    this.sendDebugLogToRemote([JSON.stringify(logArr[i])], logType)
                  }else{
                    this.sendDebugLogToRemote(["nodeType: " + logArr[i].nodeType], logType)
                  }
                else{
                  this.sendDebugLogToRemote([logArr[i]], logType)
                }
              }
            }

            return true;
          }
        }
      }
    }catch(err){
      console.warn('evaluateDebugLog error.', err);
    }
    return false;
  }

  showStoredLogs(){
    const keepLogs: any[] = [];
    this.storedLogs.forEach(l => {
      if(!this.evaluateDebugLog(l[0], l[1], l[2])){
        keepLogs.push(l);
      }
    })
    this.storedLogs = keepLogs;
  }
  
  dispatchLoggerEvent(params: Object = {}) {
    const insertEvent: CustomEvent = new CustomEvent(LoggerEventType.DISPATCH_LOG, {
      bubbles: false,
      detail: params
    })
    window.dispatchEvent(insertEvent)
  }

  getDebugParamValue(param: string) {
    const excludeLowerCase: DebugURLParams[] = [DebugURLParams.OVERRIDE_GROUP_ID, DebugURLParams.OVERRIDE_CONFIGS_GROUP_ID];
    let paramValue: string = this.paramArray.find(x => x.indexOf(param)>=0)?.split('=')?.[1] ?? this.cxInjectorWindowDebug?.[param];
    
    if(typeof paramValue !== 'undefined' && paramValue !== null && !excludeLowerCase.includes(param as DebugURLParams)){
      paramValue = paramValue.toLowerCase();
    }
    const returnParam: string = paramValue || null;
    const logType: LogType = (returnParam !== null)?LogType.QA:LogType.CXO;
    this.evaluateDebugLog(`getDebugParamValue: ${param} = ${returnParam} `, logType);
    return returnParam;
  }

  get cxInjectorWindowDebug(): CXInjectorWindowDebug{
    let debugReturn: CXInjectorWindowDebug = window['cxInjectorWindowDebug'] ?? this.topWindow['cxInjectorWindowDebug'] ?? {};
    return debugReturn;
  }
  
}